import { useState, useEffect, useRef } from 'react';
import { Drawer, Tabs, Timeline, Spin, Button, Modal, Form, Select, Input, message, theme } from 'antd';
import { laborFeeCostBearers, DepartmentManagers, SeniorManagers, FinanceManager, FinanceSeniorManager, CurrencyList, bossName, ProjectManagers } from '@utils/constants';
import { laborFeeAmountResultApi, laborFeeApprovalProcessListApi, laborFeeSubmitApprovalApi, laborFeeSubmitApprovalStatusApi, laborFeeListApi } from '@services/SereviseConst';
import './SubmitAuditDrawer.less';
import { numberProject } from '@/utils/utils';

const getTimelineStatusClassName = (approvalStatus, stepNodeId) => {
    switch (approvalStatus) {
        case 2:
            if (stepNodeId === 7 || stepNodeId === 9) {
                return 'timeline-status--warning';
            }
            return 'timeline-status--success';
        case 1:
            return 'timeline-status--warning';
        case 3:
            if (stepNodeId === 7 || stepNodeId === 9) {
                return 'timeline-status--success';
            }
            if (stepNodeId === 10) {
                return 'timeline-status--success';
            }
            return 'timeline-status--error';
        case 4:
            return 'timeline-status--error';
        case null:
            if (stepNodeId === -1) {
                return 'timeline-status--success';
            }
            return 'timeline-status--default';
        default:
            return 'timeline-status--default';
    }
};

const SubmitAuditDrawer = ({ open, onClose, conferenceId, nodeId = null, onApprovalSuccess, currentBatch = {}, batchNo = null }) => {
    const { token } = theme.useToken();
    const [submitAuditData, setSubmitAuditData] = useState(null);
    const [paymentFlowData, setPaymentFlowData] = useState([]);
    const [paymentFlowLoading, setPaymentFlowLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [approvalModalVisible, setApprovalModalVisible] = useState(false);
    const [approvalModalType, setApprovalModalType] = useState(null); // 'agree' | 'disagree'
    const [revokeModalVisible, setRevokeModalVisible] = useState(false); // 撤销申请Modal
    const revokeNodeRef = useRef(null); // 撤销申请节点
    const [approvalForm] = Form.useForm();
    const [revokeForm] = Form.useForm();
    const [form] = Form.useForm();

    // 格式化货币
    const formatCurrency = value => {
        let result = '';
        const f = parseFloat(value) || 0.0;
        const parts = f.toFixed(2).split('.');
        result = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? '.' + parts[1] : '');
        return result;
    };

    // 根据币种代码获取货币单位信息
    const getCurrencyInfo = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency
            ? {
                  majorUnit: currency.majorUnit,
                  minorUnits: currency.minorUnits,
              }
            : {
                  majorUnit: '元',
                  minorUnits: ['角', '分'],
              };
    };

    // 数字金额转大写（带货币单位）
    const convertNumberToChineseCurrencyWithUnit = (num, unit = '元', minorUnits = ['角', '分']) => {
        const chnNumChar = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];

        const numStr = (parseFloat(num) || 0).toFixed(2);
        const [intPart, decimalPart] = numStr.split('.');
        const intNum = parseInt(intPart, 10);

        // 处理四位以内的数字转换
        const convertSectionToWords = num => {
            if (num === 0) return '';

            let result = '';
            const qian = Math.floor(num / 1000);
            const bai = Math.floor((num % 1000) / 100);
            const shi = Math.floor((num % 100) / 10);
            const ge = num % 10;

            if (qian > 0) {
                result += chnNumChar[qian] + '仟';
            }
            if (bai > 0) {
                result += chnNumChar[bai] + '佰';
            } else if (qian > 0 && (shi > 0 || ge > 0)) {
                result += '零';
            }
            if (shi > 0) {
                result += chnNumChar[shi] + '拾';
            } else if ((qian > 0 || bai > 0) && ge > 0) {
                if (shi === 0 && bai === 0) {
                    result += '零';
                }
            }
            if (ge > 0) {
                result += chnNumChar[ge];
            }

            return result;
        };

        // 处理万位以上的数字
        if (intNum === 0) {
            return `零${unit}整`;
        }

        let result = '';

        // 处理万位及以上
        if (intNum >= 10000) {
            const wan = Math.floor(intNum / 10000);
            const remainder = intNum % 10000;

            result += convertSectionToWords(wan) + '万';

            if (remainder > 0) {
                if (remainder < 1000) {
                    result += '零';
                }
                result += convertSectionToWords(remainder);
            }
        } else {
            result = convertSectionToWords(intNum);
        }

        // 清理多余的零
        result = result.replace(/零+/g, '零').replace(/零$/g, '').replace(/^零/, '');

        result += unit;

        // 处理小数部分
        if (decimalPart) {
            const minorUnit1 = minorUnits[0] || '角'; // 默认为角
            const minorUnit2 = minorUnits[1] || '分'; // 默认为分

            const digit1 = parseInt(decimalPart[0], 10);
            const digit2 = parseInt(decimalPart[1], 10);

            if (digit1 === 0 && digit2 === 0) {
                result += '整';
            } else {
                if (digit1 > 0) {
                    result += chnNumChar[digit1] + minorUnit1;
                } else {
                    if (digit2 > 0) {
                        result += '零';
                    }
                }
                if (digit2 > 0) {
                    result += chnNumChar[digit2] + minorUnit2;
                }
            }
        } else {
            result += '整';
        }

        return result;
    };

    // 根据币种代码转换数字金额为大写
    const convertNumberToChineseCurrencyByCurrency = (num, currencyCode) => {
        const { majorUnit, minorUnits } = getCurrencyInfo(currencyCode);
        return convertNumberToChineseCurrencyWithUnit(num, majorUnit, minorUnits);
    };

    // 获取提交审批数据
    const getSubmitAuditData = async () => {
        try {
            const res = await laborFeeAmountResultApi({
                projectId: conferenceId,
                batchNo,
            });
            if (res.status_code === 200) {
                const data = res?.data;

                // 处理费用承担主体数据
                const costBearers = data?.costBearers || [];
                const formattedCostBearers = costBearers.map(item => {
                    const costBearerValue = item.costBearer !== undefined && item.costBearer !== null ? item.costBearer : null;

                    // 查找外币字段
                    let foreignCurrencyField = null;
                    let foreignCurrencyValue = null;
                    let currency = 'CNY';

                    for (const field in item) {
                        if (field !== 'laborFeeAmount' && field !== 'individualTaxAmount' && field !== 'costBearer' && field !== 'expertSize' && field !== 'currency' && CurrencyList.some(c => c.currency === field)) {
                            foreignCurrencyField = field;
                            foreignCurrencyValue = item[field];
                            currency = field;
                            break;
                        }
                    }

                    const isForeignCurrency = currency !== 'CNY' && CurrencyList.some(c => c.currency === currency);
                    const baseCostBearerName = costBearerValue !== null ? laborFeeCostBearers.find(b => b.value === costBearerValue)?.label || '—' : '—';
                    const costBearerName = isForeignCurrency ? `${baseCostBearerName}（外币）` : baseCostBearerName;

                    return {
                        ...item,
                        costBearer: costBearerValue,
                        costBearerName: costBearerName,
                        currency: currency, // 保存货币信息
                        isForeignCurrency: isForeignCurrency, // 标记是否为外币
                        foreignCurrencyField: foreignCurrencyField, // 记录外币字段名
                        foreignCurrencyValue: parseFloat(foreignCurrencyValue) || 0,
                    };
                });

                setSubmitAuditData({ ...data, costBearers: formattedCostBearers });
            }
        } catch (error) {
            console.error('获取审批数据失败', error);
        }
    };

    // 获取付款流程
    const fetchPaymentFlow = async () => {
        if (!conferenceId) return;

        setPaymentFlowLoading(true);
        try {
            const res = await laborFeeApprovalProcessListApi({
                conferenceId,
                batchNo,
            });
            if (res.status_code === 200) {
                setPaymentFlowData(Array.isArray(res.data) ? res.data : []);
            }
        } catch (error) {
            console.error('获取付款流程失败:', error);
        } finally {
            setPaymentFlowLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            getSubmitAuditData();
            fetchPaymentFlow();
        }
    }, [open]);

    // 获取默认的付款流程数据
    const getDefaultPaymentFlow = () => {
        return [
            {
                nodeId: 1,
                nodeName: '经办人发起',
                approvalStatus: 1, // 1-待审核（待提交）
                approvalByName: null,
                approvalAt: null,
                approvalStatusName: '待提交',
            },
        ];
    };

    // 付款流程 Tab
    const PaymentFlowTab = () => {
        if (paymentFlowLoading) {
            return (
                <div className="payment-flow-tab--loading">
                    <Spin size="large" />
                </div>
            );
        }

        // 如果付款流程数据为空，使用默认数据
        let dataToDisplay = paymentFlowData && paymentFlowData.length > 0 ? [...paymentFlowData].reverse() : getDefaultPaymentFlow();

        // 如果第一条数据的状态是3（不同意），则只展示第一条，且当前第一条显示待提交（页面覆盖接口状态）
        if (dataToDisplay.length > 0 && dataToDisplay[0].approvalStatus === 3) {
            dataToDisplay = [{ ...dataToDisplay[0], approvalStatus: 1, approvalStatusName: '待提交', approvalByName: null }];
        }

        return (
            <div className="payment-flow-tab">
                <Timeline className="payment-flow-container">
                    {dataToDisplay.map(step => {
                        const statusClassName = getTimelineStatusClassName(step.approvalStatus, step.nodeId);
                        const statusLabel = step.approvalStatusName;
                        const isSubmitted = step.nodeId === -1 || step.approvalStatus === 2 || (step.nodeId === 10 && step.approvalStatus === 3);
                        const icon = isSubmitted ? <i className="bi bi-check-circle"></i> : <i className="bi bi-clock"></i>;
                        return (
                            <Timeline.Item key={step.nodeId} dot={icon}>
                                <div className="timeline-content">
                                    <div className="timeline-content__header">
                                        <span className="timeline-content__node-name">{step.nodeName}</span>
                                        <span className={`timeline-content__status ${statusClassName}`}>{statusLabel}</span>
                                    </div>
                                    {step.approvalByName && (
                                        <div className="timeline-content__meta">
                                            <span className="timeline-content__approver">{step.approvalByName}</span>
                                            <span className="timeline-content__time">{step.approvalAt || ''}</span>
                                        </div>
                                    )}
                                    {step.rejectedReason && (
                                        <div className="timeline-content__remark">
                                            <div>{step.rejectedReason}</div>
                                        </div>
                                    )}
                                </div>
                            </Timeline.Item>
                        );
                    })}
                </Timeline>
            </div>
        );
    };

    // 获取含税金额
    const getTotalAmount = (LaborFeeSum, IndividualTaxSum) => {
        if (LaborFeeSum !== null && IndividualTaxSum !== null) {
            return (Number(LaborFeeSum) + Number(IndividualTaxSum)).toFixed(2);
        }
    };

    // 部门负责人选项数据（从常量获取）
    const departmentLeaderOptions = DepartmentManagers.map(manager => ({
        value: manager.phone,
        label: manager.name,
    }));

    // 项目负责人选项数据（从常量获取，与部门负责人相同）
    const projectManagerOptions = ProjectManagers.map(manager => ({
        value: manager.phone,
        label: manager.name,
    }));

    // 分管高管选项数据（从常量获取）
    const executiveOptions = SeniorManagers.map(manager => ({
        value: manager.phone,
        label: manager.name,
    }));

    // 获取含税劳务费合计
    const getTaxIncludedTotal = () => {
        const laborFeeSum = parseFloat(submitAuditData?.LaborFeeSum) || 0;
        const individualTaxSum = parseFloat(submitAuditData?.IndividualTaxSum) || 0;
        return laborFeeSum + individualTaxSum;
    };

    // 处理提交审批点击
    const handleSubmitAudit = async () => {
        // 先检查是否有未填写劳务费的专家
        try {
            const params = {
                projectCode: 'YHY',
                projectId: conferenceId,
                batchNo: batchNo,
                hasLaborFee: 0, // 0表示未填写
                page: 1,
                pageSize: 50,
            };

            const res = await laborFeeListApi(params);
            if (res && res.status_code === 200) {
                const unFilledExperts = res.data?.list || [];

                if (unFilledExperts.length > 0) {
                    // 获取未填写劳务费的专家姓名
                    const expertNames = unFilledExperts.map(expert => expert.memUsername).join('、');

                    // 显示二次确认弹框
                    Modal.warning({
                        title: '提示',
                        content: (
                            <div>
                                以下专家未填写本次劳务费，请确认填写后再提交：
                                <br />
                                <strong>{expertNames}</strong>
                            </div>
                        ),
                        okText: '知道了',
                        onOk: () => {
                            // 用户点击知道了后，不执行提交操作
                        },
                        mask: { closable: true },
                        keyboard: true,
                    });

                    return; // 阻止后续提交流程
                }
            }
        } catch (error) {
            console.error('检查未填写劳务费专家失败:', error);
            // 如果检查失败，仍然允许提交，但记录错误
            message.error('检查未填写劳务费专家时出现错误，但仍可继续提交');
        }

        // 没有未填写的专家，或者检查过程中出现错误，继续提交流程
        setModalVisible(true);
        form.resetFields();
    };

    // 处理 Modal 确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            // 验证通过，弹出二次确认框
            Modal.confirm({
                title: '提示',
                content: '请确认提交审批，确认后不允许修改。',
                okText: '确认',
                cancelText: '取消',
                onOk() {
                    submitApprovalForm(values);
                },
                onCancel() {
                    // 用户点击取消
                },
            });
        } catch (errorInfo) {
            console.log('表单验证失败:', errorInfo);
        }
    };

    // 构建审批流程数据并提交
    const submitApprovalForm = async formValues => {
        // 劳务费流程节点 process nodes
        // 1 经办人发起
        // 2 部门负责人审核
        // 11 项目负责人审核
        // 3 分管高管审批
        // 4 总裁审批
        // 5 财务经理审核
        // 6 财务总监审批
        // 10 出纳付款

        try {
            const approvalFlow = ['']; // 数组第一位默认是经办人 - 传空字符

            // 11: 项目负责人
            const projectManagerPhone = formValues.projectManager ? `${formValues.projectManager}:11` : '';
            approvalFlow.push(projectManagerPhone);

            // 2: 部门负责人
            const departmentLeaderPhone = formValues.departmentLeader ? `${formValues.departmentLeader}:2` : '';
            approvalFlow.push(departmentLeaderPhone);

            // 3: 分管高管
            const executivePhone = formValues.executive ? `${formValues.executive}:3` : '';
            approvalFlow.push(executivePhone);

            // 4: 总裁（含税劳务费≥10万时，则取Boss的phone，否则下标5就是空字符）
            const bossPhone = SeniorManagers.find(m => m.name === bossName)?.phone;
            const presidentPhone = getTaxIncludedTotal() >= 100000 ? `${bossPhone}:4` : '';
            approvalFlow.push(presidentPhone);

            // 5: 财务经理
            approvalFlow.push(`${FinanceManager.phone}:5`);

            // 6: 财务总监
            approvalFlow.push(`${FinanceSeniorManager.phone}:6`);

            // 构建请求参数
            const params = {
                approvalFlow,
                nodeId: 1, // 经办人发起审批 - nodeId为1
                projectId: conferenceId,
                status: 1, // 1-同意
                batchNo: batchNo || '', // 批次号
            };

            console.log('提交审批参数:', params);

            // 调用API提交
            const res = await laborFeeSubmitApprovalApi(params);
            if (res.status_code === 200) {
                message.success('提交审批成功');
                setModalVisible(false);
                // 提交审批成功后刷新付款流程
                onApprovalSuccess();
                onClose();
            }
        } catch (error) {
            console.error('提交审批异常:', error);
            message.error('提交审批异常，请重试');
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
    };

    // 处理审批不同意
    const handleDisagree = () => {
        setApprovalModalType('disagree');
        setApprovalModalVisible(true);
        approvalForm.resetFields();
    };

    // 处理审批同意
    const handleAgree = () => {
        setApprovalModalType('agree');
        setApprovalModalVisible(true);
        approvalForm.resetFields();
    };

    // 处理撤销申请点击
    const handleRevoke = () => {
        setRevokeModalVisible(true);
        revokeForm.resetFields();
    };

    // 处理审批Modal确认
    const handleApprovalModalOk = async () => {
        try {
            const values = await approvalForm.validateFields();
            const reason = values.rejectedReason;

            // 二次确认
            const confirmMsg = approvalModalType === 'disagree' ? '确认后将退回至经办人' : '请确认审批无误，确认后不允许修改';
            Modal.confirm({
                title: '提示',
                content: confirmMsg,
                okText: '确认',
                cancelText: '取消',
                onOk() {
                    submitApprovalResult(reason);
                },
                onCancel() {
                    // 用户点击取消
                },
            });
        } catch (errorInfo) {
            console.log('表单验证失败:', errorInfo);
        }
    };

    // 提交审批结果
    const submitApprovalResult = async reason => {
        try {
            let currentNodeId = nodeId;
            const userPhone = getCurrentUserPhone();

            // 只在 nodeId 为 2 或 3 或 4 或 11 的节点中查找，获取相同手机号的待审核节点（为当前节点）
            const matchedNodes = paymentFlowData.filter(item => (item.nodeId === 2 || item.nodeId === 3 || item.nodeId === 4 || item.nodeId === 11) && item.approvalBy === userPhone && item.approvalStatus === 1);
            if (matchedNodes && matchedNodes.length > 0) {
                const matchedNode = matchedNodes[matchedNodes.length - 1];
                currentNodeId = matchedNode.nodeId;
            }

            // 若待审核节点不存在，则只在 nodeId 为 2 或 3 或 4 或 11 的节点中查找，获取相同手机号的同意节点（为当前节点）
            if (!matchedNodes.length) {
                const nodes = paymentFlowData.filter(item => (item.nodeId === 2 || item.nodeId === 3 || item.nodeId === 4 || item.nodeId === 11) && item.approvalBy === userPhone && item.approvalStatus === 2);
                if (nodes && nodes.length > 0) {
                    const targetNode = nodes[nodes.length - 1];
                    currentNodeId = targetNode.nodeId;
                }
            }

            // 构建请求参数
            const params = {
                nodeId: currentNodeId,
                projectId: conferenceId,
                status: approvalModalType === 'agree' ? 1 : 2, // 1-同意, 2-不同意
                batchNo: batchNo || '', // 批次号
                rejectedReason: reason || '',
            };

            // 调用API提交审批
            const res = await laborFeeSubmitApprovalStatusApi(params);
            if (res.status_code === 200) {
                const info = approvalModalType === 'disagree' ? '审批结束，已退回至经办人' : currentNodeId === 2 ? '审批结束，已提交高管审批' : '审批结束，已提交财务经理审核审批';
                message.success(info);
                setApprovalModalVisible(false);
                // 审批成功后刷新付款流程
                onApprovalSuccess();
                onClose();
            }
        } catch (error) {
            console.error('审批失败', error);
            message.error('审批失败，请重试');
        }
    };

    const handleApprovalModalCancel = () => {
        setApprovalModalVisible(false);
        approvalForm.resetFields();
    };

    // 处理撤销Modal确认
    const handleRevokeModalOk = async () => {
        try {
            const values = await revokeForm.validateFields();
            const reason = values.rejectedReason;

            // 二次确认
            Modal.confirm({
                title: '提示',
                content: '确认后已审批节点将重置，并退回至未提交审批状态，确认提交？',
                okText: '确认',
                cancelText: '取消',
                onOk() {
                    submitRevoke(reason);
                },
                onCancel() {
                    // 用户点击取消
                },
            });
        } catch (errorInfo) {
            console.log('表单验证失败:', errorInfo);
        }
    };

    // 提交撤销申请
    const submitRevoke = async reason => {
        try {
            // 构建请求参数
            const params = {
                nodeId: 1, // 经办人撤销申请
                projectId: conferenceId,
                status: 2, // 2-撤销申请（不同意）
                batchNo: batchNo || '', // 批次号
                rejectedReason: reason || '',
            };

            // console.log('撤销申请参数:', params);

            // 调用API撤销
            const res = await laborFeeSubmitApprovalStatusApi(params);
            if (res.status_code === 200) {
                message.success('撤销申请成功，已退回至未提交审批状态');
                setRevokeModalVisible(false);
                // 撤销成功后刷新付款流程
                onApprovalSuccess();
                onClose();
            } else {
                message.error(res.message || '撤销申请失败');
            }
        } catch (error) {
            console.error('撤销申请失败', error);
            message.error('撤销申请失败，请重试');
        }
    };

    const handleRevokeModalCancel = () => {
        setRevokeModalVisible(false);
        revokeForm.resetFields();
    };

    // 获取当前登录用户的手机号
    const getCurrentUserPhone = () => {
        const adminInfo = localStorage.getItem('infos');
        let userPhone = null;
        if (adminInfo && JSON.parse(adminInfo)) {
            const info = JSON.parse(adminInfo)?.data;
            userPhone = JSON.parse(info)?.phone;
        }
        return userPhone;
    };

    // 根据审批流程数据动态生成footer
    const renderFooter = () => {
        // currentBatch.nodeId 不存在时 || 当前付款批次经办人待提交时，则显示 "提交申请"
        if (!currentBatch?.nodeId || (currentBatch?.nodeId === 1 && currentBatch?.approvalStatus === 1)) {
            return (
                <div style={{ textAlign: 'right' }}>
                    <Button type="primary" onClick={handleSubmitAudit}>
                        提交申请
                    </Button>
                </div>
            );
        }

        // mode !== 'submit' 的情况
        if (!paymentFlowData || paymentFlowData.length === 0) {
            return null;
        }

        const userPhone = getCurrentUserPhone();

        // 查找节点列表中最后一个 nodeId 为 1（经办人）的节点，截取以此节点为起始的数组, 以下所有需要检查的节点都在此数组中查找
        const lastIndex = paymentFlowData.findLastIndex(item => item.nodeId === 1);
        const newNodes = paymentFlowData.slice(lastIndex);

        // 检查 nodeId 为 5（财务经理）的节点，如果存在多个财务经理且每一个都是 approvalStatus === 1（待审批）时，则不展示任何按钮
        const financeManagerNodes = newNodes.filter(item => item.nodeId === 5);
        if (financeManagerNodes && financeManagerNodes.length > 0 && financeManagerNodes.every(item => item.approvalStatus === 1)) {
            return null;
        }

        // 检查nodeId 为 1（经办人）或 nodeId 为 2（部门负责人）或 nodeId 为 3（分管高管）的节点 或 nodeId 为4（总裁）的节点 或 项目负责人（11）的节点
        // 查找待审核状态的节点
        const currentRevokeNode = newNodes.find(item => (item.nodeId === 1 || item.nodeId === 2 || item.nodeId === 3 || item.nodeId === 4 || item.nodeId === 11) && item.approvalStatus === 1);

        // 检查 nodeId 为 2（部门负责人）或 nodeId 为 3（分管高管）的节点 或 nodeId 为4（总裁）的节点 或 项目负责人（11）
        // 查找待审核状态的节点
        const oneNode = newNodes.find(item => (item.nodeId === 2 || item.nodeId === 3 || item.nodeId === 4 || item.nodeId === 11) && item.approvalStatus === 1);

        // 查找同意状态，且兼容手机号相同的最后一个节点
        const applicableNode = newNodes.filter(item => (item.nodeId === 2 || item.nodeId === 3 || item.nodeId === 4) && item.approvalStatus === 2);
        const twoNode = applicableNode && applicableNode.length > 0 ? applicableNode[applicableNode.length - 1] : null;

        const buttons = [];

        // 1、如果项目节点(1,2,3,4,11)待审批且当前登录用户是经办人，显示撤销申请按钮
        // 经办人对应 nodeId 为 1，需要检查当前用户手机号是否与经办人手机号一致
        if (currentRevokeNode) {
            revokeNodeRef.current = currentRevokeNode;
            // 查找节点列表中 nodeId 为 1（经办人）的节点
            const operatorNode = newNodes.find(item => item.nodeId === 1);
            // 如果当前用户是经办人，显示撤销申请按钮
            if (operatorNode && operatorNode.approvalBy === userPhone) {
                buttons.push(
                    <Button key="revoke" danger onClick={handleRevoke}>
                        撤销申请
                    </Button>,
                );
            }
        }

        // 2、如果当前节点是待审核节点，且当前节点手机号与登录用户手机号一致，则显示 "不同意" 和 "同意"
        if (oneNode && oneNode.approvalBy === userPhone) {
            buttons.push(
                <Button key="disagree" danger onClick={handleDisagree} style={{ marginRight: 8 }}>
                    不同意
                </Button>,
                <Button key="agree" type="primary" onClick={handleAgree}>
                    同意
                </Button>,
            );
        }
        // 3、如果当前节点是同意状态，且当前节点手机号与登录用户手机号一致，则显示 "不同意"
        else if (twoNode && twoNode.approvalBy === userPhone) {
            buttons.push(
                <Button key="disagree" danger onClick={handleDisagree}>
                    不同意
                </Button>,
            );
        }

        if (buttons.length === 0) {
            return null;
        }

        return <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>{buttons}</div>;
    };

    return (
        <Drawer
            title={nodeId === 1 ? '提交审批详情' : '审批详情'}
            placement="bottom"
            onClose={onClose}
            open={open}
            size="100vh"
            styles={{
                body: { padding: 0, overflow: 'hidden' },
                header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
                wrapper: { height: '100vh' },
            }}
            footer={renderFooter()}
            className="submit-audit-drawer"
            closable={{ placement: 'end' }}
        >
            <div className="submit-audit-drawer__layout">
                <div className="submit-audit-drawer__main">
                    <div className="payment-batch-info">
                        <div className="submit-audit-drawer__module-title">付款申请批次信息</div>
                        <div className="module-content">
                            <div>
                                <div>
                                    付款批次：<span>{currentBatch?.batchCreatedAt || '—'}</span>
                                </div>
                            </div>
                            <div>
                                <div>
                                    经办人：<span>{submitAuditData?.createdBy || '—'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 项目信息 */}
                    <div className="labor-project-info">
                        <div className="submit-audit-drawer__module-title">项目信息</div>
                        <div className="module-content">
                            <div className="full-width-row">
                                <div>
                                    项目名称：<span>{submitAuditData?.name || '—'}</span>
                                </div>
                            </div>
                            <div>
                                <div>
                                    氚云编号：<span>{submitAuditData?.h3yunNo || '—'}</span>
                                </div>
                            </div>
                            <div>
                                <div>
                                    项目编号：<span>{numberProject('YHY', conferenceId)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 审批信息 */}
                    <div className="approval-info">
                        <div className="submit-audit-drawer__module-title">付款信息</div>
                        <div className="module-content">
                            <div className="two-column-row">
                                <div>
                                    <div>
                                        本批次专家总数：<span>{submitAuditData?.expertSum || '—'}人</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        需要劳务费人数：<span>{submitAuditData?.realTotal || '—'}人</span>
                                    </div>
                                </div>
                            </div>
                            <div className="two-column-row">
                                <div>
                                    <div>
                                        实发劳务费合计金额（元）：<span>{formatCurrency(submitAuditData?.LaborFeeSum)}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(submitAuditData?.LaborFeeSum || 0)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="two-column-row">
                                <div>
                                    <div>
                                        个人所得税合计金额（元）：<span>{formatCurrency(submitAuditData?.IndividualTaxSum)}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(submitAuditData?.IndividualTaxSum || 0)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="two-column-row">
                                <div>
                                    <div>
                                        含税劳务费合计金额（元）：<span>{formatCurrency(getTotalAmount(submitAuditData?.LaborFeeSum, submitAuditData?.IndividualTaxSum))}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(getTotalAmount(submitAuditData?.LaborFeeSum, submitAuditData?.IndividualTaxSum))}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {submitAuditData?.costBearers
                        && submitAuditData?.costBearers.length > 0
                        && submitAuditData.costBearers.map(costBearerItem => {
                            // 如果是外币，使用外币相关的数据
                            const laborFeeAmount = parseFloat(costBearerItem.laborFeeAmount) || 0;
                            const individualTaxAmount = parseFloat(costBearerItem.individualTaxAmount) || 0;
                            const totalAmount = (laborFeeAmount + individualTaxAmount).toFixed(2);

                            // 确定货币单位
                            const currencyUnit = costBearerItem.isForeignCurrency ? CurrencyList.find(c => c.currency === costBearerItem.currency)?.name || costBearerItem.currency : '元';

                            return (
                                <div
                                    key={`cost_bearer_${costBearerItem.costBearer}_${costBearerItem.isForeignCurrency ? `${laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}（外币）` : laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}`}
                                    className="cost-bearers"
                                >
                                    <div className="submit-audit-drawer__module-title">支付主体：{costBearerItem.isForeignCurrency ? `${laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}（外币）` : laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}</div>
                                    <div className="module-content">
                                        <div className="two-column-row">
                                            <div>
                                                {' '}
                                                需要劳务费人数：<span>{costBearerItem.expertSize}人</span>
                                            </div>
                                        </div>
                                        <div></div>
                                        {costBearerItem.isForeignCurrency ? (
                                            // 外币支付时的展示项
                                            <>
                                                <div className="two-column-row">
                                                    <div>
                                                        外币实发额合计（{currencyUnit}）：<span>{formatCurrency(costBearerItem.foreignCurrencyValue)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {currencyUnit}
                                                            {convertNumberToChineseCurrencyByCurrency(costBearerItem.foreignCurrencyValue, costBearerItem.currency)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="two-column-row">
                                                    <div>
                                                        折合人民币实发劳务费合计金额（元）：<span>{formatCurrency(laborFeeAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(laborFeeAmount)}</span>
                                                    </div>
                                                </div>
                                                <div className="two-column-row">
                                                    <div>
                                                        折合人民币个人所得税合计金额（元）：<span>{formatCurrency(individualTaxAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(individualTaxAmount)}</span>
                                                    </div>
                                                </div>
                                                <div className="two-column-row">
                                                    <div>
                                                        折合人民币含税劳务费合计金额（元）：<span>{formatCurrency(totalAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(totalAmount)}</span>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            // 非外币支付时的展示项
                                            <>
                                                <div className="two-column-row">
                                                    <div>
                                                        实发劳务费合计金额（元）：<span>{formatCurrency(laborFeeAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(laborFeeAmount)}</span>
                                                    </div>
                                                </div>
                                                <div className="two-column-row">
                                                    <div>
                                                        个人所得税合计金额（元）：<span>{formatCurrency(individualTaxAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(individualTaxAmount)}</span>
                                                    </div>
                                                </div>
                                                <div className="two-column-row">
                                                    <div>
                                                        含税劳务费合计金额（元）：<span>{formatCurrency(totalAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：<span>人民币{convertNumberToChineseCurrencyWithUnit(totalAmount)}</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                </div>

                {/* 右侧：两个Tab */}
                <div className="custom-tab-container">
                    <Tabs
                        defaultActiveKey="payment"
                        items={[
                            {
                                key: 'payment',
                                label: '付款流程',
                                children: <PaymentFlowTab />,
                            },
                        ]}
                    />
                </div>
            </div>

            {/* 提交申请 Modal */}
            <Modal title="提交申请" open={modalVisible} onOk={handleModalOk} onCancel={handleModalCancel} okText="确认" cancelText="取消" width={600}>
                <Form form={form} autoComplete="off" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                    <Form.Item label="项目负责人" name="projectManager" rules={[{ required: true, message: '请选择项目负责人' }]} extra="请选择本项目商务经理或健康界企业对接人">
                        <Select placeholder="请选择项目负责人" options={projectManagerOptions} showSearch={{ filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} />
                    </Form.Item>

                    <Form.Item label="部门负责人" name="departmentLeader" rules={[{ required: true, message: '请选择部门负责人' }]}>
                        <Select placeholder="请选择部门负责人" options={departmentLeaderOptions} showSearch={{ filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} />
                    </Form.Item>

                    <Form.Item label="分管高管" name="executive" rules={[{ required: true, message: '请选择分管高管' }]}>
                        <Select placeholder="请选择分管高管" options={executiveOptions} showSearch={{ filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) }} />
                    </Form.Item>

                    {getTaxIncludedTotal() >= 100000 && (
                        <Form.Item
                            label={
                                <>
                                    <span className="submit-audit-drawer__required-mark">*</span> 总裁
                                </>
                            }
                        >
                            <span>{bossName}</span>
                        </Form.Item>
                    )}

                    <Form.Item
                        label={
                            <>
                                <span className="submit-audit-drawer__required-mark">*</span> 财务经理
                            </>
                        }
                    >
                        <span>{FinanceManager.name}</span>
                    </Form.Item>

                    <Form.Item
                        label={
                            <>
                                <span className="submit-audit-drawer__required-mark">*</span> 财务总监
                            </>
                        }
                    >
                        <span>{FinanceSeniorManager.name}</span>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 审批意见 Modal */}
            <Modal title={approvalModalType === 'disagree' ? '审核不同意意见' : '审核同意意见'} open={approvalModalVisible} onOk={handleApprovalModalOk} onCancel={handleApprovalModalCancel} okText="确认" cancelText="取消" width={600}>
                <Form form={approvalForm} autoComplete="off" layout="vertical">
                    <Form.Item label={approvalModalType === 'disagree' ? '不同意理由（确认后该劳务费申请，将退回至经办人）' : '同意理由'} name="rejectedReason" rules={[{ required: approvalModalType === 'disagree' ? true : false, message: `请输入${approvalModalType === 'disagree' ? '不' : ''}同意理由` }]}>
                        <Input.TextArea rows={4} placeholder={`请输入${approvalModalType === 'disagree' ? '不' : ''}同意理由`} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 撤销申请 Modal */}
            <Modal title="撤销申请" open={revokeModalVisible} onOk={handleRevokeModalOk} onCancel={handleRevokeModalCancel} okText="确认" cancelText="取消" width={600}>
                <Form form={revokeForm} autoComplete="off" layout="vertical">
                    <Form.Item label="撤销理由" name="rejectedReason" rules={[{ required: true, message: '请输入撤销理由' }]}>
                        <Input.TextArea rows={4} placeholder="请输入撤销理由" />
                    </Form.Item>
                </Form>
            </Modal>
        </Drawer>
    );
};

export default SubmitAuditDrawer;
